import styled from 'styled-components'
import { Handle } from '@xyflow/react'
import { TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'

// ============================================================================
// BUTTON HANDLE DESIGN
// A handle that extends from the node edge with a dotted line and button at the end
// The button appears on hover, creating a clear "add node" affordance
// ============================================================================

const HANDLE_CONFIG = {
  lineLength: 24, // Length of the dotted line extending from node
  buttonSize: 28, // Size of the circular button
  lineThickness: 2, // Thickness of the dotted line
  dashPattern: '4 3', // Dash pattern for the dotted line
  hoverScale: 1.1, // Scale factor on hover
}

// Right-side handle wrapper (for adding children)
// No scaling here - only the button scales to keep the line connection stable
export const HandleWrapper = styled.div<{ $accentColor?: string }>`
  position: absolute;
  right: -${HANDLE_CONFIG.lineLength + HANDLE_CONFIG.buttonSize}px;
  top: 50%;
  transform: translateY(-50%);
  width: ${HANDLE_CONFIG.lineLength + HANDLE_CONFIG.buttonSize}px;
  height: ${HANDLE_CONFIG.buttonSize}px;
  z-index: 10;
  cursor: pointer;
  display: flex;
  align-items: center;
`

// Left-side handle wrapper (for adding parents)
// No scaling here - only the button scales to keep the line connection stable
export const LeftHandleWrapper = styled.div<{ $accentColor?: string }>`
  position: absolute;
  left: -${HANDLE_CONFIG.lineLength + HANDLE_CONFIG.buttonSize}px;
  top: 50%;
  transform: translateY(-50%);
  width: ${HANDLE_CONFIG.lineLength + HANDLE_CONFIG.buttonSize}px;
  height: ${HANDLE_CONFIG.buttonSize}px;
  z-index: 10;
  cursor: pointer;
  display: flex;
  align-items: center;
`

// Solid line extending from the node edge
export const HandleLine = styled.div<{ $accentColor: string; $direction: 'left' | 'right' }>`
  width: ${HANDLE_CONFIG.lineLength}px;
  height: ${HANDLE_CONFIG.lineThickness}px;
  background-color: ${(props) => props.$accentColor};
  flex-shrink: 0;
`

// Circular button at the end of the handle - inverted colors (themed bg, colored icon)
// Fixed size - no scaling to ensure edges connect properly to the center
export const HandleButton = styled.div<{ $accentColor: string }>`
  width: ${HANDLE_CONFIG.buttonSize}px;
  height: ${HANDLE_CONFIG.buttonSize}px;
  background-color: var(--color-node-bg);
  border: 2px solid ${(props) => props.$accentColor};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  box-shadow: var(--shadow-handle);
  transition:
    box-shadow 0.2s ease,
    transform 0.15s ease,
    background-color 0.15s ease;

  ${HandleWrapper}:hover &,
    ${LeftHandleWrapper}:hover & {
    transform: scale(${HANDLE_CONFIG.hoverScale});
    box-shadow: 0 4px 12px ${(props) => props.$accentColor}40;
    background-color: ${(props) => props.$accentColor}10;
  }

  &:active {
    transform: scale(0.95);
  }
`

// Icon container inside the button
export const HandleButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

// Invisible target handle - receives connections but doesn't allow drag initiation
// Use isConnectableStart={false} on the component to prevent dragging from this handle
export const InvisibleTargetHandle = styled(Handle)`
  opacity: 0;
  width: 24px;
  height: 24px;
  left: -12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`

// Source handle - invisible, overlapping the button for drag-to-connect
// The visual is provided by HandleButton, this just enables React Flow connections
export const SourceHandle = styled(Handle)<{ $accentColor?: string }>`
  opacity: 0;
  width: ${HANDLE_CONFIG.buttonSize}px;
  height: ${HANDLE_CONFIG.buttonSize}px;
  cursor: crosshair;
  pointer-events: auto;
  position: absolute;
  top: 0;
  left: 0;
  right: unset;
  bottom: unset;
  transform: none;
`

// Source handle for left side - invisible, for drag-to-connect
export const LeftSourceHandle = styled(Handle)<{ $accentColor?: string }>`
  opacity: 0;
  width: ${HANDLE_CONFIG.buttonSize}px;
  height: ${HANDLE_CONFIG.buttonSize}px;
  cursor: pointer;
  pointer-events: auto;
  position: absolute;
  top: 0;
  left: 0;
  right: unset;
  bottom: unset;
  transform: none;
`

// ============================================================================
// FLOATING AI SUGGESTION BADGE
// A small floating indicator that appears above the handle button when AI suggestions
// are available. Shows the AI sparkle icon and a count of available suggestions.
// ============================================================================

// Container for the floating AI badge - positioned above and centered on HandleButton
export const FloatingAIBadge = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  background: transparent;
  z-index: 15;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }
`

// Count text inside the badge - adapts to theme
export const FloatingAIBadgeCount = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  line-height: 1;
  position: relative;
`
